import { Payment } from '@api/raisenow';
import { Decimal, Numeric } from '@fmnts/core/decimal';
import { PaymentInterval } from '@formunauts/shared/campaigning/domain';
import { isString } from 'effect/Predicate';

/**
 * Formats the given `value` to a `number` as it is expected to be
 * passed to the raisenow API.
 *
 * @param value
 * The value to format.
 * Might be anything that is understood by the `Decimal` constructor.
 *
 * @returns
 * Formatted value
 */
export function formatAmount(value: Numeric): number {
  return new Decimal(value).times(100).toDecimalPlaces(0).toNumber();
}

/**
 * @returns
 * Formatted currency code for raisw now
 */
export function formatCurrencyCode(currencyCode: unknown): string {
  return isString(currencyCode) ? currencyCode.toLowerCase() : '';
}

export function getRecurringIntervalCode(
  referenceDate: string,
  intervalKey: PaymentInterval.PaymentInterval,
): Payment.RecurringInterval {
  const day = new Date(referenceDate).getDate();
  const month = new Date(referenceDate).getMonth() + 1;
  let recurringInterval = null;
  switch (intervalKey) {
    case '0':
      recurringInterval = 'monthly';
      break;
    case '1':
      recurringInterval = 'quarterly';
      break;
    case '2':
      recurringInterval = 'semestral';
      break;
    case '3':
      recurringInterval = 'yearly';
      break;
    default:
      return null;
  }
  let recurringIntervalCode = null;
  let payments = null;
  switch (recurringInterval) {
    case 'monthly':
      recurringIntervalCode = day + ' * *';
      break;
    case 'quarterly':
      payments = [month, month + 3, month + 6, month + 9];
      for (const i in payments) {
        if (payments[i] > 12) {
          payments[i] = payments[i] - 12;
        }
      }
      recurringIntervalCode = day + ' ' + payments.join(',') + ' *';
      break;
    case 'semestral':
      payments = [month, month + 6];
      for (const i in payments) {
        if (payments[i] > 12) {
          payments[i] = payments[i] - 12;
        }
      }
      recurringIntervalCode = day + ' ' + payments.join(',') + ' *';
      break;
    case 'yearly':
      recurringIntervalCode = day + ' ' + month + ' *';
      break;
  }
  return recurringIntervalCode;
}

export function getSextantSalutation(titleCode: string): string {
  const label = (codeLabels as Partial<typeof codeLabels>)[
    titleCode as keyof typeof codeLabels
  ];
  const salutation =
    label &&
    (RAISENOW_SALUTATION_CHOICES as Record<string, string | undefined>)[label];
  return salutation || RAISENOW_SALUTATION_CHOICES['SALUTATION_OTHER'];
}

const RAISENOW_SALUTATION_CHOICES = {
  /* eslint-disable @typescript-eslint/naming-convention */
  SALUTATION_MALE: 'mr',
  SALUTATION_FEMALE: 'ms',
  SALUTATION_FEMALE_UNKNOWN: 'ms',
  SALUTATION_FEMALE_UNMARRIED: 'ms',
  SALUTATION_FAMILY: 'family',
  SALUTATION_COMPANY: 'none',
  SALUTATION_OTHER: 'other',
  /* eslint-enable @typescript-eslint/naming-convention */
} as const;
const codeLabels = {
  '0': 'SALUTATION_MALE',
  '1': 'SALUTATION_FEMALE',
  '2': 'SALUTATION_FAMILY',
  '3': 'SALUTATION_COMPANY',
  '4': 'SALUTATION_OTHER',
  '5': 'SALUTATION_FEMALE_UNKNOWN',
  '6': 'SALUTATION_FEMALE_UNMARRIED',
  '7': 'SALUTATION_NONE',
  '8': 'SALUTATION_DR',
} as const;
